import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import moment from 'moment';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Row,
  Input,
  FormGroup,
  Label,
} from 'reactstrap';
import { saveAs } from 'file-saver';

import { useClearListStateOnPageChange, useTableControls } from 'hooks';
import BorderlineCandidateCollection, { BorderlineCandidateDto } from 'lib/common/models/borderlineCandidateCollection';
import { UserTypesLabels } from 'lib/common/models/user';
import 'react-toastify/dist/ReactToastify.css';
import { stateListNames } from 'store/reducers/listsReducer';
import LoadingSpinner from 'views/components/loadingSpinner/loadingSpinner';
import ReactTable from '../../../../components/ReactTable/ReactTable';
import ApiCaller from '../../../../lib/ApiCaller';
import { AppState } from '../../../../store/store';
import CandidateApi from '../../../../api/CandidateApi';
import ExamCenterApi from '../../../../api/ExamCenterApi';
import ExamCenterCollection from '../../../../lib/common/models/examCenterCollection';

interface BorderlineFilters {
  dateFrom?: string;
  dateTo?: string;
  examCenterId?: string;
  candidateId?: string;
  candidateName?: string;
}

const BorderlineCandidates: React.FC = () => {
  const loggedUser = useSelector((state: AppState) => state.session.userInfo);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingTip, setLoadingTip] = useState<string>('');

  const [borderlineCandidates, setBorderlineCandidates] = useState<BorderlineCandidateCollection>(
    new BorderlineCandidateCollection([]),
  );
  const [examCenters, setExamCenters] = useState<ExamCenterCollection>(
    new ExamCenterCollection([]),
  );

  // Local state for input values (updates immediately in the UI)
  const [localFilters, setLocalFilters] = useState<BorderlineFilters>(() => {
    if (loggedUser.type === UserTypesLabels.examCentre && loggedUser.entityId) {
      return { examCenterId: loggedUser.entityId };
    }
    return {};
  });

  // State for the actual filters that trigger the search (with debounce)
  const [filters, setFilters] = useState<BorderlineFilters>(localFilters);

  // useEffect with debounce to update filters when localFilters changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters(localFilters);
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [localFilters]);

  const listName = stateListNames.borderlineCandidatesList;
  const {
    filters: tableFilters,
    sorting,
    pagination,
    onChangeFilters,
    onChangeSorting,
    onChangePagination,
  } = useTableControls(listName);

  useClearListStateOnPageChange(listName, ['/borderline/candidates']);

  useEffect(() => {
    loadData();
    loadExamCenters();
  }, [filters]);

  const loadData = async () => {
    setLoadingTip('Loading borderline candidates...');
    setLoading(true);

    try {
      const candidateApi = new CandidateApi(new ApiCaller(loggedUser.token));
      const result = await candidateApi.getBorderlineCandidates(filters);

      if (result && result.length() > 0) {
        setBorderlineCandidates(result);
        toast.success(`${result.length()} borderline candidates loaded successfully`);
      } else {
        setBorderlineCandidates(new BorderlineCandidateCollection([]));
        toast.info('No borderline candidates found with current filters');
      }
    } catch (error) {
      console.error(error);
      toast.error('Error loading borderline candidates');
    } finally {
      setLoading(false);
    }
  };

  const loadExamCenters = async () => {
    try {
      const examCenterApi = new ExamCenterApi(new ApiCaller(loggedUser.token));
      const centers = await examCenterApi.getExamCentersList();
      if (centers) {
        setExamCenters(centers);
      }
    } catch (error) {
      console.error('Error loading exam centers:', error);
    }
  };

  const handleFilterChange = (field: keyof BorderlineFilters, value: string) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value || undefined,
    }));
  };

  const clearFilters = () => {
    // Keep exam center ID if user is an exam center
    const clearedFilters = loggedUser.type === UserTypesLabels.examCentre && loggedUser.entityId
      ? { examCenterId: loggedUser.entityId }
      : {};
    
    setLocalFilters(clearedFilters);
    setFilters(clearedFilters);
  };

  const exportToCSV = () => {
    const csvData = borderlineCandidates.candidates.map(candidate => ({
      'Exam Number': candidate.examNumber || '-',
      'Candidate ID': candidate.candidateSerialNumber || '-',
      'Candidate Name': `${candidate.candidateFirstName} ${candidate.candidateLastName}`,
      'Exam Date': moment(candidate.examDate).format('DD-MM-YYYY'),
      'Exam Center': candidate.examCenterName || '-',
    }));

    const csvContent = [
      Object.keys(csvData[0] || {}).join(','),
      ...csvData.map(row => Object.values(row).map(value =>
        typeof value === 'string' && value.includes(',') ? `"${value}"` : value
      ).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `borderline-candidates-${moment().format('YYYY-MM-DD')}.csv`);
    toast.success('Report exported successfully');
  };

  const columns = [
    {
      Header: 'EXAM NUMBER',
      accessor: (d: BorderlineCandidateDto) => d.examNumber || '-',
      sortable: true,
    },
    {
      Header: 'CANDIDATE ID',
      accessor: (d: BorderlineCandidateDto) => d.candidateSerialNumber || '-',
      sortable: true,
    },
    {
      Header: 'CANDIDATE NAME',
      accessor: (d: BorderlineCandidateDto) => `${d.candidateFirstName} ${d.candidateLastName}`,
      sortable: true,
    },
    {
      Header: 'EXAM DATE',
      accessor: (d: BorderlineCandidateDto) => moment(d.examDate).format('DD-MM-YYYY'),
      sortable: true,
    },
    {
      Header: 'EXAM CENTER',
      accessor: (d: BorderlineCandidateDto) => d.examCenterName || '-',
      sortable: true,
    },
  ];

  // Filter exam centers based on user role
  const availableExamCenters = loggedUser.type === UserTypesLabels.examCentre
    ? examCenters.centers.filter(ec => ec._id === loggedUser.entityId)
    : examCenters.centers;

  return (
    <>
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">Borderline Candidates Dashboard</CardTitle>
              </CardHeader>
              <CardBody>
                {/* Filters Section */}
                <Card className="mb-3">
                  <CardBody>
                    <Row>
                      <Col md="3">
                        <FormGroup>
                          <Label for="dateFrom">From Date</Label>
                          <Input
                            type="date"
                            id="dateFrom"
                            value={localFilters.dateFrom || ''}
                            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label for="dateTo">To Date</Label>
                          <Input
                            type="date"
                            id="dateTo"
                            value={localFilters.dateTo || ''}
                            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                          />
                        </FormGroup>
                      </Col>
                      {loggedUser.type !== UserTypesLabels.examCentre && (
                        <Col md="3">
                          <FormGroup>
                            <Label for="examCenter">Exam Center</Label>
                            <Input
                              type="select"
                              id="examCenter"
                              value={localFilters.examCenterId || ''}
                              onChange={(e) => handleFilterChange('examCenterId', e.target.value)}
                              style={{ width: '100%', maxWidth: '100%' }}
                            >
                              <option value="">All Centers</option>
                              {availableExamCenters.map(center => (
                                <option key={center._id} value={center._id}>
                                  {center.name}
                                </option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>
                      )}
                      <Col md="3">
                        <FormGroup>
                          <Label for="candidateId">Candidate ID</Label>
                          <Input
                            type="text"
                            id="candidateId"
                            placeholder="Search by candidate ID"
                            value={localFilters.candidateId || ''}
                            onChange={(e) => handleFilterChange('candidateId', e.target.value)}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="3">
                        <FormGroup>
                          <Label for="candidateName">Candidate Name</Label>
                          <Input
                            type="text"
                            id="candidateName"
                            placeholder="Search by candidate name"
                            value={localFilters.candidateName || ''}
                            onChange={(e) => handleFilterChange('candidateName', e.target.value)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="9" className="d-flex align-items-end">
                        <Button color="secondary" onClick={clearFilters} className="mr-2">
                          Clear Filters
                        </Button>
                        <Button
                          color="success"
                          onClick={exportToCSV}
                          disabled={borderlineCandidates.length() === 0}
                        >
                          <i className="nc-icon nc-cloud-download-93" />
                          Export CSV
                        </Button>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* Results Section */}
                <LoadingSpinner spinning={loading} tip={loadingTip}>
                  <ReactTable
                    data={borderlineCandidates?.candidates || []}
                    columns={columns}
                    initialFilters={tableFilters}
                    onChangeFilters={onChangeFilters}
                    initialSorting={sorting}
                    onChangeSorting={onChangeSorting}
                    initialPagination={pagination}
                    onChangePagination={onChangePagination}
                  />
                </LoadingSpinner>

                {borderlineCandidates.length() === 0 && !loading && (
                  <div className="text-center mt-4">
                    <p className="text-muted">No borderline candidates found. Try adjusting your filters.</p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      <ToastContainer />
    </>
  );
};

export default BorderlineCandidates;
